<script setup lang="ts">
  import AlertDialog from '@/components/ui/alert-dialog/AlertDialog.vue'
  import AlertDialogAction from '@/components/ui/alert-dialog/AlertDialogAction.vue'
  import AlertDialogCancel from '@/components/ui/alert-dialog/AlertDialogCancel.vue'
  import AlertDialogContent from '@/components/ui/alert-dialog/AlertDialogContent.vue'
  import AlertDialogDescription from '@/components/ui/alert-dialog/AlertDialogDescription.vue'
  import AlertDialogFooter from '@/components/ui/alert-dialog/AlertDialogFooter.vue'
  import AlertDialogHeader from '@/components/ui/alert-dialog/AlertDialogHeader.vue'
  import AlertDialogTitle from '@/components/ui/alert-dialog/AlertDialogTitle.vue'
  import { useTranslation } from '@/composables/useTranslation'

  const { t } = useTranslation({
    en: {
      unsavedEmail: 'Unsaved Email',
      unsavedEmailDescription:
        'You have an unsaved email draft. Are you sure you want to proceed and discard the draft?',
      backToDraft: 'Back to Draft',
      discardDraft: 'Discard Draft'
    },
    de: {
      unsavedEmail: 'Ungespeicherte E-Mail',
      unsavedEmailDescription:
        'Sie haben einen ungespeicherten E-Mail-Entwurf. Möchten Sie wirklich fortfahren und den Entwurf verwerfen?',
      backToDraft: 'Zurück zum Entwurf',
      discardDraft: 'Entwurf verwerfen'
    }
  })

  const props = defineProps<{
    show: boolean
  }>()

  const emit = defineEmits<{
    (e: 'confirm'): void
    (e: 'cancel'): void
    (e: 'update:show', value: boolean): void
  }>()

  const handleConfirm = () => {
    emit('confirm')
    emit('update:show', false)
  }

  const handleCancel = () => {
    emit('cancel')
    emit('update:show', false)
  }
</script>

<template>
  <AlertDialog
    :open="show"
    @update:open="$emit('update:show', $event)"
  >
    <AlertDialogContent class="unsaved-email-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle class="dialog-title">{{ t('unsavedEmail') }}</AlertDialogTitle>
        <div class="purple-divider"></div>
        <AlertDialogDescription class="dialog-description">
          {{ t('unsavedEmailDescription') }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter class="dialog-footer">
        <AlertDialogCancel
          class="cancel-button"
          @click="handleCancel"
        >
          {{ t('backToDraft') }}
        </AlertDialogCancel>
        <AlertDialogAction
          class="confirm-button"
          @click="handleConfirm"
        >
          {{ t('discardDraft') }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>

<style scoped>
  .unsaved-email-dialog {
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
    padding: 1.5rem;
    max-width: 500px;
  }

  .dialog-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #4b4b4b;
    margin-bottom: 0.75rem;
  }

  .purple-divider {
    height: 3px;
    background: linear-gradient(90deg, #7e3af2 0%, #a78bfa 100%);
    width: 50px;
    margin-bottom: 1rem;
    border-radius: 2px;
  }

  .dialog-description {
    font-size: 1rem;
    line-height: 1.6;
    color: #6b7280;
    margin-bottom: 1.5rem;
  }

  .dialog-footer {
    display: flex;
    gap: 1rem;
    margin-top: 1rem;
  }

  .cancel-button {
    border: 1px solid #d1d5db;
    background-color: white;
    color: #4b5563;
    font-weight: 500;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    transition: all 0.2s ease;
  }

  .cancel-button:hover {
    background-color: #f3f4f6;
    border-color: #9ca3af;
  }

  .confirm-button {
    background: linear-gradient(135deg, #7e3af2 0%, #6025c0 100%);
    color: white;
    font-weight: 500;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    border: none;
    box-shadow: 0 2px 8px rgba(126, 58, 242, 0.25);
    transition: all 0.2s ease;
  }

  .confirm-button:hover {
    background: linear-gradient(135deg, #8b4af2 0%, #6b2dd6 100%);
    box-shadow: 0 4px 12px rgba(126, 58, 242, 0.35);
    transform: translateY(-1px);
  }
</style>
